import { Injectable, signal, computed, effect } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'dashboard-theme';
  
  // Theme state signal, default to dark for this dashboard
  private currentTheme = signal<'light' | 'dark'>('dark');
  
  // Expose read-only signals for components
  readonly theme = this.currentTheme.asReadonly();
  readonly isDark = computed(() => this.currentTheme() === 'dark');
  
  constructor() {
    this.initializeTheme();
    
    // Set up an effect to automatically sync theme state with the DOM & localStorage
    effect(() => {
      const theme = this.currentTheme();
      this.applyTheme(theme);
    });
  }
  
  /**
   * Toggle between dark and light themes
   */
  toggleTheme(): void {
    this.currentTheme.update(current => current === 'light' ? 'dark' : 'light');
  }
  
  /**
   * Set theme explicitly
   */
  setTheme(theme: 'light' | 'dark'): void {
    this.currentTheme.set(theme);
  }
  
  /**
   * Check local storage or system preference to initialize the theme state
   */
  private initializeTheme(): void {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem(this.THEME_KEY) as 'light' | 'dark' | null;
      
      if (savedTheme === 'light' || savedTheme === 'dark') {
        this.currentTheme.set(savedTheme);
      } else {
        // Fall back to system preference or default to dark
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.currentTheme.set(systemPrefersDark ? 'dark' : 'light');
      }
    }
  }
  
  /**
   * Apply the theme classes to the document element and persist in localStorage
   */
  private applyTheme(theme: 'light' | 'dark'): void {
    if (typeof window === 'undefined') return;
    
    const root = window.document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
    
    localStorage.setItem(this.THEME_KEY, theme);
  }
}
